import CryptoJS from 'crypto-js';

/** Matches backend RequestSecurityUtil.APP_NUMBER_PATTERN. */
export const APPLICATION_NUMBER_PLAIN_PATTERN = /^[A-Za-z0-9-]{3,40}$/;

export const isPlainApplicationNumber = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed.length > 0 && APPLICATION_NUMBER_PLAIN_PATTERN.test(trimmed);
};

const getJourneyEncryptionKey = () => {
  const key = import.meta.env.VITE_JOURNEY_QUERY_ENCRYPTION_KEY;
  return typeof key === 'string' && key.trim() ? key.trim() : null;
};

export const encryptJourneyQueryValue = (plainText) => {
  const key = getJourneyEncryptionKey();
  const value = String(plainText ?? '').trim();
  if (!value) return '';

  // Only encrypt values that are already valid plain identifiers (e.g. N0002459).
  if (!isPlainApplicationNumber(value)) return '';

  // If key is not configured, fall back to plain value to avoid breaking the journey resume flow.
  if (!key) return value;

  return CryptoJS.AES.encrypt(value, key).toString();
};

/** Encrypt DOB and other sensitive query/body fields (not limited to application-number pattern). */
export const encryptJourneySensitiveValue = (plainText) => {
  const key = getJourneyEncryptionKey();
  const value = String(plainText ?? '').trim();
  if (!value) return '';
  if (!key) return value;
  return CryptoJS.AES.encrypt(value, key).toString();
};

export const decryptJourneyQueryValue = (cipherText) => {
  const key = getJourneyEncryptionKey();
  const value = String(cipherText ?? '').trim();
  if (!value) return '';
  if (!key) return value;

  try {
    const bytes = CryptoJS.AES.decrypt(value, key);
    const plain = bytes.toString(CryptoJS.enc.Utf8);
    return plain || '';
  } catch {
    return '';
  }
};

/**
 * Normalize an application number from URL, API, storage, or Redux.
 * Redux and session storage always use `plain`; URLs use `encrypted`.
 *
 * Never maps undecryptable ciphertext to `plain` (avoids double-encryption on submitLead).
 */
export const resolveJourneyApplicationNumbers = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return { plain: null, encrypted: null };
  }

  if (isPlainApplicationNumber(trimmed)) {
    return {
      plain: trimmed,
      encrypted: encryptJourneyQueryValue(trimmed) || trimmed,
    };
  }

  const decrypted = decryptJourneyQueryValue(trimmed);
  if (isPlainApplicationNumber(decrypted)) {
    return {
      plain: decrypted,
      encrypted: encryptJourneyQueryValue(decrypted) || trimmed,
    };
  }

  return {
    plain: null,
    encrypted: trimmed,
  };
};
